<template>
  <div class="p-grid">
    <Card class="style p-col" style="width: 100%">
      <template #title>
        <div class="p-d-flex p-jc-between">
          <h3 class="p-m-0">
            <strong>{{ pageTitle }}</strong>
          </h3>
          <ToggleButton
            v-model="isFiltersOpen"
            v-tooltip.bottom="_t('label_Display_filters')"
            class="p-button-rounded p-button-text p-button-plain"
            offIcon="pi pi-filter"
            onIcon="pi pi-filter"
            style="font-size: 16px"
          />
        </div>
      </template>

      <template #content>
        <TabView v-model:activeIndex="activeTab" @tab-click="switchTab">
          <TabPanel header="Kernel status">
            <DataTable
              :value="products"
              tableStyle="min-width: 50rem; width: 50%;"
            >
              <Column
                field="webStatus"
                header="Web status"
                style="padding-left: 14px !important; width: 6.5rem"
              ></Column>
              <Column
                field="actualStatus"
                header="Actual status"
                style="padding-left: 14px !important; max-width: 11.5rem"
              >
                <template #body>
                  <Dropdown
                    v-model="selectedVariant"
                    :options="variants"
                    @change="(event) => updateStatus(event.value)"
                  />
                </template>
              </Column>
              <Column
                field="date"
                header="Date"
                style="padding-left: 14px !important; width: 12.5rem"
              >
              </Column>
              <Column
                field="quantity"
                header="Quantity"
                style="padding-left: 14px !important"
              >
                <template #body>
                  <InputText v-model="input" @input="onInput" style="width: 100%" />
                </template>
              </Column>
            </DataTable>
          </TabPanel>
          <TabPanel header="Frontend">
            <DataTable
              :value="frontData"
              tableStyle="min-width: 50rem; width: 50%;"
            >
              <Column
                field="name"
                header="Name"
                style="padding-left: 14px !important"
              ></Column>
              <Column
                field="state"
                header="State"
                style="padding-left: 14px !important"
              ></Column>
              <Column
                field="action"
                header="Action"
                style="padding-left: 14px !important"
              >
                <template #body="{ data, field }">
                  <div
                    style="display: flex; flex-wrap: wrap; gap: 10px"
                    v-if="data.name === 'HTTP Connector'"
                  >
                    <Button
                      v-tooltip.bottom="'Refresh'"
                      icon="mdi mdi-repeat"
                      class="p-button-rounded p-button-text p-button-secondary"
                    />
                    <Button
                      v-tooltip.bottom="'Start'"
                      icon="mdi mdi-play"
                      class="p-button-rounded p-button-text p-button-secondary"
                    />
                    <Button
                      v-tooltip.bottom="'Stop'"
                      icon="mdi mdi-stop"
                      class="p-button-rounded p-button-text p-button-secondary"
                    />
                    <Button
                      v-tooltip.bottom="'Restart'"
                      icon="mdi mdi-restart"
                      class="p-button-rounded p-button-text p-button-secondary"
                    />
                  </div>
                  <span v-else>{{ data[field] }}</span>

                  <div
                    style="display: flex; flex-wrap: wrap; gap: 10px"
                    v-if="data.name === 'HTTPS Connector'"
                  >
                    <Button
                      v-tooltip.bottom="'Refresh'"
                      icon="mdi mdi-repeat"
                      class="p-button-rounded p-button-text p-button-secondary"
                    />
                    <Button
                      v-tooltip.bottom="'Start'"
                      icon="mdi mdi-play"
                      class="p-button-rounded p-button-text p-button-secondary"
                    />
                    <Button
                      v-tooltip.bottom="'Stop'"
                      icon="mdi mdi-stop"
                      class="p-button-rounded p-button-text p-button-secondary"
                    />
                    <Button
                      v-tooltip.bottom="'Restart'"
                      icon="mdi mdi-restart"
                      class="p-button-rounded p-button-text p-button-secondary"
                    />
                  </div>
                  <span v-else>{{ data[field] }}</span>

                  <div
                    style="display: flex; flex-wrap: wrap; gap: 10px"
                    v-if="data.name === 'SSL Certificate'"
                  >
                    <Button
                      v-tooltip.bottom="'Refresh'"
                      icon="mdi mdi-repeat"
                      class="p-button-rounded p-button-text p-button-secondary"
                      @click="getCertificates"
                    />
                    <Button
                      v-tooltip.bottom="'Generate'"
                      icon="mdi mdi-database-plus"
                      class="p-button-rounded p-button-text p-button-secondary"
                      :loading="SSLCertificateLoading"
                      @click="generateSSLCertificate"
                    />
                  </div>
                  <span v-else>{{ data[field] }}</span>
                </template>
              </Column>
            </DataTable>
          </TabPanel>
          <TabPanel header="Backend">
            <backend-config :isFiltersOpen="isFiltersOpen" />
          </TabPanel>
          <TabPanel header="Interface">
            <web-interface />
          </TabPanel>
        </TabView>
      </template>
    </Card>
  </div>
</template>

<script>
import { getAdminDashboard, updateAdminDashboardStatus } from "@/api/dashboard";
import { generateCertificates, getCertificates } from "@/api/certificates";
import webInterface from "../front/web_interface";
import backendConfig from "../back";
import {debounce} from "lodash";

export default {
  name: "general-dashboard",
  components: {
    webInterface,
    backendConfig,
  },
  mounted() {
    this.frontData = [
      {
        id: "0",
        name: "HTTP Connector",
        state: "STARTED",
        action: "",
      },
      {
        id: "1",
        name: "HTTPS Connector",
        state: "STARTED",
        action: "",
      },
      {
        id: "2",
        name: "SSL Certificate",
        state: "1",
        action: "",
      },
    ];
    this.getCertificates();
    this.getDashboard();

    const tab = this.$route.query.tab ?? "0";
    if (tab) {
      this.activeTab = parseInt(tab);
      this.$router.push("/server?tab=" + tab);
    }
  },
  watch: {
    "$route.query.tab"() {
      const tab = this.$route.query.tab ?? "0";
      this.activeTab = parseInt(tab);
    },
  },
  data() {
    return {
      products: null,
      frontData: null,
      variants: ["ACTV", "PASV", "WARNING", "MAINTENANCE", "GOING_ALIVE"],
      selectedVariant: "ACTV",
      input: "",
      activeTab: 0,
      SSLCertificateLoading: false,
      isFiltersOpen: true,
    };
  },
  created() {
    this.debouncedInputHandler = debounce(this.handleInput, 500);
  },
  methods: {
    onInput(event) {
      this.debouncedInputHandler(event.target.value);
    },
    handleInput() {
      this.updateStatus(this.selectedVariant, this.input)
    },
    getFormattedDate() {
      const now = new Date();

      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");

      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.0`;
    },
    getDashboard() {
      getAdminDashboard().then((response) => {
        this.products = [
          {
            id: 0,
            webStatus: response.data.webServicesAlive ? "Active" : "Down",
            actualStatus: "",
            date: this.getFormattedDate(),
            quantity: "",
          },
        ];
        this.selectedVariant = response.data.status;
      });
    },
    updateStatus(status, timeoutMins) {
      updateAdminDashboardStatus(status, timeoutMins).then(() => this.getDashboard());
    },
    getCertificates() {
      getCertificates().then(
        (response) => (this.frontData[2].state = response.data)
      );
    },
    generateSSLCertificate() {
      this.SSLCertificateLoading = true;
      generateCertificates().finally(
        () => (this.SSLCertificateLoading = false)
      );
    },
    switchTab(value) {
      this.$router.push(`/server?tab=${value.index}`);
    },
  },
  computed: {
    pageTitle() {
      switch (this.activeTab) {
        case 0:
          return "Server dashboard";
        case 1:
          return "Frontend";
        case 2:
          return "Backend";
        default:
          return "Interface";
      }
    },
  },
};
</script>

<style>
.p-button-icon {
  font-size: 15px !important;
}
</style>
